import { useEffect, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import PhotoUploader from "@/components/PhotoUploader";
import { Buildings, Plus, UserCircle, GraduationCap, UsersThree, Sparkle, Copy, Gear, PencilSimple, Trash, ChartBar, DownloadSimple } from "@phosphor-icons/react";

const CREATABLE_ROLES = [
  "director","study_director","secretary","accountant","cashier","hr",
  "teacher","supervisor","librarian","stock_manager","parent","auditor",
];
const ROLE_FR = {
  super_admin: "Super admin", owner: "Promoteur", director: "Directeur",
  study_director: "Directeur études", secretary: "Secrétaire", accountant: "Comptable",
  cashier: "Caissier", hr: "RH", teacher: "Enseignant", supervisor: "Surveillant",
  librarian: "Bibliothécaire", stock_manager: "Stock", parent: "Parent",
  student: "Élève", auditor: "Auditeur",
};

const resolveUrl = (u) => u ? (u.startsWith("http") || u.startsWith("data:") ? u : `${API_BASE.replace("/api", "")}${u}`) : null;

export default function AdminPanel() {
  const [tab, setTab] = useState("schools");
  const [overview, setOverview] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [openFull, setOpenFull] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [openClone, setOpenClone] = useState(false);
  const [cloneForm, setCloneForm] = useState({
    source_school_id: "", new_school: { name: "", currency: "XOF", academic_year: "2025-2026", primary_color: "#c14a2c" },
    director_email: "", director_name: "", director_password: "Clone2026!",
  });

  // Manage-school dialog state
  const [openManage, setOpenManage] = useState(false);
  const [manageSchool, setManageSchool] = useState(null);
  const [manageUsers, setManageUsers] = useState([]);
  const [manageForm, setManageForm] = useState(null);
  const [manageTab, setManageTab] = useState("info");
  const [openAddUser, setOpenAddUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ email: "", password: "", name: "", role: "teacher", phone: "", avatar_url: "" });
  const [fullForm, setFullForm] = useState({
    school: { name: "", address: "Ouagadougou", phone: "", email: "", currency: "XOF",
              academic_year: "2025-2026", motto: "", logo_url: "", welcome_image_url: "" },
    director_email: "", director_name: "", director_password: "Demo2026!",
    include_demo_users: true, include_students: true, include_classes: true,
    students_count: 12,
  });
  const [form, setForm] = useState({
    school: { name: "", address: "", phone: "", email: "", currency: "XOF",
              academic_year: "2025-2026", motto: "", logo_url: "", welcome_image_url: "" },
    director_email: "", director_name: "", director_password: "", director_phone: "",
  });

  const load = async () => {
    try {
      const [ov, us] = await Promise.all([
        api.get("/admin/schools-overview"),
        api.get("/users"),
      ]);
      setOverview(ov.data);
      setAllUsers(us.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      if (!form.school.name || !form.director_email || !form.director_password) {
        return toast.error("Nom école + email/mot de passe directeur requis");
      }
      await api.post("/admin/schools-with-director", form);
      toast.success("Établissement créé + directeur activé");
      setOpen(false);
      setForm({
        school: { name: "", address: "", phone: "", email: "", currency: "XOF",
                  academic_year: "2025-2026", motto: "", logo_url: "", welcome_image_url: "" },
        director_email: "", director_name: "", director_password: "", director_phone: "",
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const seedFull = async () => {
    if (!fullForm.school.name || !fullForm.director_email || !fullForm.director_name) {
      return toast.error("Nom école + nom & email du directeur requis");
    }
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-full-school", fullForm);
      toast.success(
        `École "${data.school.name}" créée : ${data.classes_count} classes, ${data.students_count} élèves, ${data.team.length + 1} membres.`
      );
      setOpenFull(false);
      setFullForm({
        school: { name: "", address: "Ouagadougou", phone: "", email: "", currency: "XOF",
                  academic_year: "2025-2026", motto: "", logo_url: "", welcome_image_url: "" },
        director_email: "", director_name: "", director_password: "Demo2026!",
        include_demo_users: true, include_students: true, include_classes: true,
        students_count: 12,
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSeeding(false); }
  };

  const openCloneFor = (sourceId) => {
    setCloneForm({ ...cloneForm, source_school_id: sourceId });
    setOpenClone(true);
  };

  const cloneNow = async () => {
    if (!cloneForm.source_school_id || !cloneForm.new_school.name || !cloneForm.director_email) {
      return toast.error("École source + nom + email directeur requis");
    }
    setCloning(true);
    try {
      const { data } = await api.post("/admin/clone-school", cloneForm);
      toast.success(`École clonée : ${data.classes_cloned} classes, ${data.subjects_cloned} matières, ${data.fee_types_cloned} frais copiés.`);
      setOpenClone(false);
      setCloneForm({
        source_school_id: "", new_school: { name: "", currency: "XOF", academic_year: "2025-2026", primary_color: "#c14a2c" },
        director_email: "", director_name: "", director_password: "Clone2026!",
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCloning(false); }
  };

  const openManageFor = async (sch) => {
    setManageSchool(sch);
    setManageForm({
      name: sch.name || "", address: sch.address || "", phone: sch.phone || "", email: sch.email || "",
      currency: sch.currency || "XOF", academic_year: sch.academic_year || "2025-2026",
      motto: sch.motto || "", logo_url: sch.logo_url || "", welcome_image_url: sch.welcome_image_url || "",
      primary_color: sch.primary_color || "#c14a2c", signature_url: sch.signature_url || "",
      director_title: sch.director_title || "Le Directeur",
    });
    setManageTab("info");
    setOpenManage(true);
    try {
      const { data } = await api.get(`/users?school_id=${sch.id}`);
      setManageUsers(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveManageInfo = async () => {
    try {
      await api.patch(`/schools/${manageSchool.id}`, manageForm);
      toast.success("Établissement mis à jour");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addUserToSchool = async () => {
    if (!newUserForm.email || !newUserForm.password || !newUserForm.name) {
      return toast.error("Email, mot de passe et nom requis");
    }
    try {
      await api.post("/auth/register", { ...newUserForm, school_id: manageSchool.id });
      toast.success(`${newUserForm.name} ajouté à ${manageSchool.name}`);
      setOpenAddUser(false);
      setNewUserForm({ email: "", password: "", name: "", role: "teacher", phone: "", avatar_url: "" });
      const { data } = await api.get(`/users?school_id=${manageSchool.id}`);
      setManageUsers(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delUserFromSchool = async (u) => {
    if (!window.confirm(`Supprimer ${u.name} ?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utilisateur supprimé");
      const { data } = await api.get(`/users?school_id=${manageSchool.id}`);
      setManageUsers(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const byRole = allUsers.reduce((a, u) => { (a[u.role] = a[u.role] || []).push(u); return a; }, {});

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-accent font-semibold">Super administration</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">Panneau global</h1>
          <p className="text-sm text-muted-foreground mt-1">Créez des établissements, provisionnez leurs directeurs, supervisez tous les utilisateurs.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={openFull} onOpenChange={setOpenFull}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full border-accent text-accent hover:bg-accent/10" data-testid="btn-seed-full-school">
                <Sparkle size={16} className="mr-2" weight="fill" /> École complète (clé en main)
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="dialog-seed-full">
              <DialogHeader>
                <DialogTitle>Provisionner une école complète</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground -mt-1 mb-2">
                Crée en une action : école + plan comptable OHADA + directeur + comptable + caissier + enseignant + parent + 6 classes + 6 matières · coef · frais type + N élèves démo. Comme l'exemple École Wend-Panga.
              </p>
              <Tabs defaultValue="school">
                <TabsList>
                  <TabsTrigger value="school">1. École</TabsTrigger>
                  <TabsTrigger value="director">2. Directeur</TabsTrigger>
                  <TabsTrigger value="content">3. Contenu</TabsTrigger>
                </TabsList>
                <TabsContent value="school" className="space-y-3 mt-4">
                  <div><Label>Nom de l'école</Label><Input value={fullForm.school.name} onChange={e => setFullForm({ ...fullForm, school: { ...fullForm.school, name: e.target.value } })} placeholder="École Bogodogo" data-testid="input-full-school-name" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Téléphone</Label><Input value={fullForm.school.phone} onChange={e => setFullForm({ ...fullForm, school: { ...fullForm.school, phone: e.target.value } })} placeholder="+226 25 …" /></div>
                    <div><Label>Année scolaire</Label><Input value={fullForm.school.academic_year} onChange={e => setFullForm({ ...fullForm, school: { ...fullForm.school, academic_year: e.target.value } })} /></div>
                  </div>
                  <div><Label>Adresse</Label><Input value={fullForm.school.address} onChange={e => setFullForm({ ...fullForm, school: { ...fullForm.school, address: e.target.value } })} /></div>
                  <div><Label>Email école</Label><Input value={fullForm.school.email} onChange={e => setFullForm({ ...fullForm, school: { ...fullForm.school, email: e.target.value } })} placeholder="contact@bogodogo.bf" /></div>
                  <div><Label>Devise / Motto</Label><Input value={fullForm.school.motto} onChange={e => setFullForm({ ...fullForm, school: { ...fullForm.school, motto: e.target.value } })} placeholder="Éduquer pour bâtir" /></div>
                </TabsContent>
                <TabsContent value="director" className="space-y-3 mt-4">
                  <div><Label>Nom complet du directeur</Label><Input value={fullForm.director_name} onChange={e => setFullForm({ ...fullForm, director_name: e.target.value })} placeholder="Zongo Salimata" data-testid="input-full-director-name" /></div>
                  <div><Label>Email du directeur</Label><Input type="email" value={fullForm.director_email} onChange={e => setFullForm({ ...fullForm, director_email: e.target.value })} placeholder="directeur@bogodogo.bf" data-testid="input-full-director-email" /></div>
                  <div><Label>Mot de passe initial (pour toute l'équipe démo)</Label><Input value={fullForm.director_password} onChange={e => setFullForm({ ...fullForm, director_password: e.target.value })} data-testid="input-full-director-password" /></div>
                  <p className="text-xs text-muted-foreground">
                    Les emails de l'équipe démo seront générés automatiquement à partir du nom de l'école :
                    <br /><code className="font-mono">comptable@[slug].bf</code>, <code className="font-mono">caissier@[slug].bf</code>, <code className="font-mono">enseignant@[slug].bf</code>, <code className="font-mono">parent@[slug].bf</code>
                  </p>
                </TabsContent>
                <TabsContent value="content" className="space-y-4 mt-4">
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-md border border-border hover:border-accent transition-colors">
                    <input type="checkbox" checked={fullForm.include_classes} onChange={e => setFullForm({ ...fullForm, include_classes: e.target.checked })} className="w-4 h-4 accent-accent" data-testid="check-classes" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">6 classes + 6 matières + 4 types de frais</div>
                      <div className="text-xs text-muted-foreground">CP1, CE1, CM2, 6ème A, 3ème B, Terminale D · Math, Français, Sciences, HG, Anglais, EPS</div>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-md border border-border hover:border-accent transition-colors">
                    <input type="checkbox" checked={fullForm.include_demo_users} onChange={e => setFullForm({ ...fullForm, include_demo_users: e.target.checked })} className="w-4 h-4 accent-accent" data-testid="check-team" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">Équipe démo (comptable, caissier, enseignant, parent)</div>
                      <div className="text-xs text-muted-foreground">4 comptes avec le mot de passe défini ci-dessus</div>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-md border border-border hover:border-accent transition-colors">
                    <input type="checkbox" checked={fullForm.include_students} onChange={e => setFullForm({ ...fullForm, include_students: e.target.checked })} className="w-4 h-4 accent-accent" data-testid="check-students" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">Élèves démo</div>
                      <div className="flex items-center gap-3 mt-2">
                        <Label className="text-xs">Nombre</Label>
                        <Input
                          type="number" min="0" max="200" className="w-24 h-8"
                          disabled={!fullForm.include_students}
                          value={fullForm.students_count}
                          onChange={e => setFullForm({ ...fullForm, students_count: Number(e.target.value) })}
                          data-testid="input-students-count"
                        />
                        <span className="text-xs text-muted-foreground">avec noms burkinabè, répartis équitablement dans les classes</span>
                      </div>
                    </div>
                  </label>
                </TabsContent>
              </Tabs>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenFull(false)}>Annuler</Button>
                <Button onClick={seedFull} disabled={seeding} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-save-seed-full">
                  <Sparkle size={14} className="mr-1" weight="fill" /> {seeding ? "Provisionnement…" : "Provisionner"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-school-admin">
              <Plus size={16} className="mr-2" /> Nouvel établissement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="dialog-new-school">
            <DialogHeader><DialogTitle>Créer un établissement + directeur</DialogTitle></DialogHeader>
            <Tabs defaultValue="school">
              <TabsList>
                <TabsTrigger value="school">1. École</TabsTrigger>
                <TabsTrigger value="director">2. Directeur</TabsTrigger>
                <TabsTrigger value="branding">3. Identité visuelle</TabsTrigger>
              </TabsList>
              <TabsContent value="school" className="space-y-3 mt-4">
                <div><Label>Nom</Label><Input value={form.school.name} onChange={e => setForm({ ...form, school: { ...form.school, name: e.target.value } })} data-testid="input-admin-school-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Téléphone</Label><Input value={form.school.phone} onChange={e => setForm({ ...form, school: { ...form.school, phone: e.target.value } })} /></div>
                  <div><Label>Année scolaire</Label><Input value={form.school.academic_year} onChange={e => setForm({ ...form, school: { ...form.school, academic_year: e.target.value } })} /></div>
                </div>
                <div><Label>Adresse</Label><Input value={form.school.address} onChange={e => setForm({ ...form, school: { ...form.school, address: e.target.value } })} /></div>
                <div><Label>Email école</Label><Input value={form.school.email} onChange={e => setForm({ ...form, school: { ...form.school, email: e.target.value } })} /></div>
                <div><Label>Devise / Motto</Label><Input value={form.school.motto} onChange={e => setForm({ ...form, school: { ...form.school, motto: e.target.value } })} /></div>
                <div className="pt-3 border-t border-border">
                  <div className="text-xs uppercase tracking-widest text-secondary font-semibold mb-2">Configuration pays & pédagogie</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Pays</Label>
                      <Select value={form.school.country || "BF"} onValueChange={v => {
                        const englishCountries = ["GH","KE","NG","UG","TZ","ZA","GB","US","AU","CA","IE"];
                        const preferred_language = englishCountries.includes(v) ? "en" : "fr";
                        setForm({ ...form, school: { ...form.school, country: v, preferred_language } });
                      }}>
                        <SelectTrigger data-testid="select-school-country"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BF">🇧🇫 Burkina Faso</SelectItem>
                          <SelectItem value="CI">🇨🇮 Côte d'Ivoire</SelectItem>
                          <SelectItem value="SN">🇸🇳 Sénégal</SelectItem>
                          <SelectItem value="ML">🇲🇱 Mali</SelectItem>
                          <SelectItem value="NE">🇳🇪 Niger</SelectItem>
                          <SelectItem value="TG">🇹🇬 Togo</SelectItem>
                          <SelectItem value="BJ">🇧🇯 Bénin</SelectItem>
                          <SelectItem value="CM">🇨🇲 Cameroun</SelectItem>
                          <SelectItem value="GH">🇬🇭 Ghana</SelectItem>
                          <SelectItem value="NG">🇳🇬 Nigeria</SelectItem>
                          <SelectItem value="KE">🇰🇪 Kenya</SelectItem>
                          <SelectItem value="RW">🇷🇼 Rwanda</SelectItem>
                          <SelectItem value="CD">🇨🇩 RD Congo</SelectItem>
                          <SelectItem value="MA">🇲🇦 Maroc</SelectItem>
                          <SelectItem value="FR">🇫🇷 France</SelectItem>
                          <SelectItem value="GB">🇬🇧 Royaume-Uni</SelectItem>
                          <SelectItem value="US">🇺🇸 États-Unis</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Devise</Label>
                      <Select value={form.school.currency || "XOF"} onValueChange={v => setForm({ ...form, school: { ...form.school, currency: v } })}>
                        <SelectTrigger data-testid="select-school-currency"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="XOF">XOF · Franc CFA (UEMOA)</SelectItem>
                          <SelectItem value="XAF">XAF · Franc CFA (CEMAC)</SelectItem>
                          <SelectItem value="GHS">GHS · Cedi ghanéen</SelectItem>
                          <SelectItem value="NGN">NGN · Naira nigérian</SelectItem>
                          <SelectItem value="KES">KES · Shilling kényan</SelectItem>
                          <SelectItem value="RWF">RWF · Franc rwandais</SelectItem>
                          <SelectItem value="CDF">CDF · Franc congolais</SelectItem>
                          <SelectItem value="MAD">MAD · Dirham marocain</SelectItem>
                          <SelectItem value="EUR">EUR · Euro</SelectItem>
                          <SelectItem value="USD">USD · Dollar US</SelectItem>
                          <SelectItem value="GBP">GBP · Livre sterling</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Système éducatif</Label>
                      <Select value={form.school.education_system || "francophone_be"} onValueChange={v => setForm({ ...form, school: { ...form.school, education_system: v } })}>
                        <SelectTrigger data-testid="select-school-education-system"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="francophone_be">Francophone (BE/CI/SN…)</SelectItem>
                          <SelectItem value="british">British (Reception → Year 13)</SelectItem>
                          <SelectItem value="ghs">Ghanéen (KG → SHS)</SelectItem>
                          <SelectItem value="naija_9-3-4">Nigérian 9-3-4</SelectItem>
                          <SelectItem value="kenya_cbc">Kenyan CBC</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Échelle de notation</Label>
                      <Select value={form.school.grading_scale || "0-20"} onValueChange={v => setForm({ ...form, school: { ...form.school, grading_scale: v } })}>
                        <SelectTrigger data-testid="select-school-grading-scale"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0-20">0 → 20 (francophone)</SelectItem>
                          <SelectItem value="0-100">0 → 100 (%)</SelectItem>
                          <SelectItem value="A-F">A → F (lettres)</SelectItem>
                          <SelectItem value="A1-F9">A1 → F9 (WAEC)</SelectItem>
                          <SelectItem value="GPA">GPA 0.0 → 4.0</SelectItem>
                          <SelectItem value="competency">Compétences (CBC)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label>Découpage des périodes</Label>
                      <Select value={form.school.period_type || "trimester"} onValueChange={v => setForm({ ...form, school: { ...form.school, period_type: v } })}>
                        <SelectTrigger data-testid="select-school-period-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="trimester">3 Trimestres</SelectItem>
                          <SelectItem value="semester">2 Semestres</SelectItem>
                          <SelectItem value="term">3 Terms (anglophone)</SelectItem>
                          <SelectItem value="quarter">4 Quarters</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-muted-foreground mt-1">Les périodes seront créées automatiquement à la première ouverture.</p>
                    </div>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="director" className="space-y-3 mt-4">
                <div><Label>Nom complet du directeur</Label><Input value={form.director_name} onChange={e => setForm({ ...form, director_name: e.target.value })} data-testid="input-director-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Email</Label><Input type="email" value={form.director_email} onChange={e => setForm({ ...form, director_email: e.target.value })} data-testid="input-director-email" /></div>
                  <div><Label>Téléphone</Label><Input value={form.director_phone} onChange={e => setForm({ ...form, director_phone: e.target.value })} /></div>
                </div>
                <div><Label>Mot de passe initial</Label><Input value={form.director_password} onChange={e => setForm({ ...form, director_password: e.target.value })} data-testid="input-director-password" /></div>
                <p className="text-xs text-muted-foreground">Le directeur pourra ensuite créer les autres profils (comptable, caissier, enseignants…) via son espace.</p>
              </TabsContent>
              <TabsContent value="branding" className="space-y-4 mt-4">
                <div>
                  <Label>Logo de l'école</Label>
                  <PhotoUploader value={form.school.logo_url} onChange={v => setForm({ ...form, school: { ...form.school, logo_url: v } })} kind="logo" size={100} shape="square" />
                </div>
                <div>
                  <Label>Image d'accueil (bannière)</Label>
                  <PhotoUploader value={form.school.welcome_image_url} onChange={v => setForm({ ...form, school: { ...form.school, welcome_image_url: v } })} kind="welcome" size={100} shape="square" />
                </div>
              </TabsContent>
            </Tabs>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={create} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-save-school-admin">Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="schools" data-testid="tab-admin-schools"><Buildings size={14} className="mr-1" /> Établissements ({overview.length})</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-admin-users"><UsersThree size={14} className="mr-1" /> Tous les utilisateurs ({allUsers.length})</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-admin-analytics"><ChartBar size={14} className="mr-1" /> Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="schools" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {overview.map(s => (
              <Card key={s.id} className="p-5 card-hover" data-testid={`card-admin-school-${s.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                    {s.logo_url ? <img src={resolveUrl(s.logo_url)} alt="" className="w-full h-full object-cover" /> : <Buildings size={26} weight="duotone" className="text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold truncate">{s.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{s.address || "—"}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{s.academic_year}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                  <div className="p-2 bg-muted rounded-md">
                    <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Élèves</div>
                    <div className="font-heading text-lg font-bold">{s.students_count}</div>
                  </div>
                  <div className="p-2 bg-muted rounded-md">
                    <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Utilisateurs</div>
                    <div className="font-heading text-lg font-bold">{s.users_count}</div>
                  </div>
                </div>
                {s.directors?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Directeurs</div>
                    {s.directors.map(d => (
                      <div key={d.id} className="flex items-center gap-2 text-xs">
                        <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center overflow-hidden">
                          {d.avatar_url ? <img src={resolveUrl(d.avatar_url)} alt="" className="w-full h-full object-cover" /> : <UserCircle size={16} weight="duotone" />}
                        </div>
                        <div className="truncate">{d.name}</div>
                        <div className="text-muted-foreground truncate">· {d.email}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-border flex justify-end gap-2">
                  <Button size="sm" onClick={() => openManageFor(s)} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid={`btn-manage-${s.id}`}>
                    <Gear size={14} className="mr-1" /> Gérer
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openCloneFor(s.id)} data-testid={`btn-clone-${s.id}`}>
                    <Copy size={14} className="mr-1" /> Cloner
                  </Button>
                </div>
              </Card>
            ))}
            {!overview.length && <div className="col-span-full text-center text-sm text-muted-foreground py-16">Aucun établissement encore. Créez le premier avec le bouton ci-dessus.</div>}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Utilisateur</th><th className="px-4 py-3">Rôle</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Établissement</th><th className="px-4 py-3">Créé</th>
              </tr></thead>
              <tbody>
                {allUsers.map((u, i) => {
                  const sch = overview.find(s => s.id === u.school_id);
                  return (
                    <tr key={u.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                            {u.avatar_url ? <img src={resolveUrl(u.avatar_url)} alt="" className="w-full h-full object-cover" /> : <UserCircle size={18} weight="duotone" className="text-muted-foreground" />}
                          </div>
                          <span className="font-medium">{u.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs uppercase tracking-wider">{u.role}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{u.email}</td>
                      <td className="px-4 py-3 text-xs">{sch?.name || <span className="text-muted-foreground italic">—</span>}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(u.created_at)}</td>
                    </tr>
                  );
                })}
                {!allUsers.length && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">Aucun utilisateur.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <AdminAnalytics />
        </TabsContent>
      </Tabs>

      <Dialog open={openClone} onOpenChange={setOpenClone}>
        <DialogContent className="max-w-lg" data-testid="dialog-clone">
          <DialogHeader><DialogTitle>Cloner la structure d'une école</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1 mb-2">
            Copie classes, matières, coefficients, types de frais et plan comptable OHADA vers un tout nouvel établissement. <strong>Ne copie ni les élèves ni les utilisateurs</strong>.
          </p>
          <div className="grid gap-3">
            <div>
              <Label>École source</Label>
              <div className="mt-1 p-2 bg-muted rounded text-sm">
                {overview.find(o => o.id === cloneForm.source_school_id)?.name || "—"}
              </div>
            </div>
            <div><Label>Nom du nouvel établissement</Label><Input value={cloneForm.new_school.name} onChange={e => setCloneForm({ ...cloneForm, new_school: { ...cloneForm.new_school, name: e.target.value } })} placeholder="École Nouvelle" data-testid="input-clone-name" /></div>
            <div><Label>Année scolaire</Label><Input value={cloneForm.new_school.academic_year} onChange={e => setCloneForm({ ...cloneForm, new_school: { ...cloneForm.new_school, academic_year: e.target.value } })} /></div>
            <div><Label>Nom du directeur</Label><Input value={cloneForm.director_name} onChange={e => setCloneForm({ ...cloneForm, director_name: e.target.value })} data-testid="input-clone-director-name" /></div>
            <div><Label>Email du directeur</Label><Input type="email" value={cloneForm.director_email} onChange={e => setCloneForm({ ...cloneForm, director_email: e.target.value })} placeholder="directeur@nouvelle.bf" data-testid="input-clone-director-email" /></div>
            <div><Label>Mot de passe initial</Label><Input value={cloneForm.director_password} onChange={e => setCloneForm({ ...cloneForm, director_password: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenClone(false)}>Annuler</Button>
            <Button onClick={cloneNow} disabled={cloning} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-do-clone">
              <Copy size={14} className="mr-1" /> {cloning ? "Clonage…" : "Cloner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage school dialog */}
      <Dialog open={openManage} onOpenChange={setOpenManage}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-manage">
          <DialogHeader>
            <DialogTitle>Gérer · {manageSchool?.name}</DialogTitle>
          </DialogHeader>
          {manageForm && (
            <Tabs value={manageTab} onValueChange={setManageTab}>
              <TabsList>
                <TabsTrigger value="info" data-testid="tab-manage-info"><Buildings size={14} className="mr-1" /> Informations</TabsTrigger>
                <TabsTrigger value="users" data-testid="tab-manage-users"><UsersThree size={14} className="mr-1" /> Utilisateurs ({manageUsers.length})</TabsTrigger>
                <TabsTrigger value="branding" data-testid="tab-manage-branding">Identité visuelle</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-3 mt-4">
                <div><Label>Nom</Label><Input value={manageForm.name} onChange={e => setManageForm({ ...manageForm, name: e.target.value })} data-testid="input-manage-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Téléphone</Label><Input value={manageForm.phone} onChange={e => setManageForm({ ...manageForm, phone: e.target.value })} /></div>
                  <div><Label>Année scolaire</Label><Input value={manageForm.academic_year} onChange={e => setManageForm({ ...manageForm, academic_year: e.target.value })} /></div>
                </div>
                <div><Label>Adresse</Label><Input value={manageForm.address} onChange={e => setManageForm({ ...manageForm, address: e.target.value })} /></div>
                <div><Label>Email école</Label><Input type="email" value={manageForm.email} onChange={e => setManageForm({ ...manageForm, email: e.target.value })} /></div>
                <div><Label>Devise / Motto</Label><Input value={manageForm.motto} onChange={e => setManageForm({ ...manageForm, motto: e.target.value })} /></div>
                <div><Label>Titre du directeur (bulletins PDF)</Label><Input value={manageForm.director_title} onChange={e => setManageForm({ ...manageForm, director_title: e.target.value })} /></div>
                <div className="flex justify-end pt-2">
                  <Button onClick={saveManageInfo} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-save-manage-info">Enregistrer</Button>
                </div>
              </TabsContent>

              <TabsContent value="users" className="mt-4">
                <div className="flex justify-end mb-3">
                  <Button size="sm" onClick={() => setOpenAddUser(true)} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-add-user-to-school">
                    <Plus size={14} className="mr-1" /> Ajouter un utilisateur
                  </Button>
                </div>
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-2">Utilisateur</th><th className="px-3 py-2">Rôle</th><th className="px-3 py-2">Email</th><th className="px-3 py-2"></th>
                    </tr></thead>
                    <tbody>
                      {manageUsers.map((u, i) => (
                        <tr key={u.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""}`} data-testid={`row-manage-user-${u.id}`}>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                                {u.avatar_url ? <img src={resolveUrl(u.avatar_url)} alt="" className="w-full h-full object-cover" /> : <UserCircle size={18} weight="duotone" className="text-muted-foreground" />}
                              </div>
                              <span className="font-medium">{u.name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs uppercase tracking-wider">{ROLE_FR[u.role] || u.role}</td>
                          <td className="px-3 py-2 text-muted-foreground text-xs">{u.email}</td>
                          <td className="px-3 py-2 text-right">
                            {u.role !== "super_admin" && u.role !== "owner" && (
                              <Button variant="ghost" size="icon" onClick={() => delUserFromSchool(u)} data-testid={`btn-del-user-${u.id}`}><Trash size={14} className="text-destructive" /></Button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!manageUsers.length && <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-foreground text-sm">Aucun utilisateur.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="branding" className="space-y-4 mt-4">
                <div>
                  <Label className="mb-2 block">Couleur d'accent</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={manageForm.primary_color} onChange={e => setManageForm({ ...manageForm, primary_color: e.target.value })} className="w-14 h-10 rounded border border-border cursor-pointer" />
                    <Input value={manageForm.primary_color} onChange={e => setManageForm({ ...manageForm, primary_color: e.target.value })} className="w-32 font-mono text-xs" />
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Logo</Label>
                  <PhotoUploader value={manageForm.logo_url} onChange={v => setManageForm({ ...manageForm, logo_url: v })} kind="logo" size={80} shape="square" />
                </div>
                <div>
                  <Label className="mb-2 block">Image d'accueil</Label>
                  <PhotoUploader value={manageForm.welcome_image_url} onChange={v => setManageForm({ ...manageForm, welcome_image_url: v })} kind="welcome" size={80} shape="square" />
                </div>
                <div>
                  <Label className="mb-2 block">Signature du directeur</Label>
                  <PhotoUploader value={manageForm.signature_url} onChange={v => setManageForm({ ...manageForm, signature_url: v })} kind="signature" size={80} shape="square" />
                </div>
                <div className="flex justify-end pt-2">
                  <Button onClick={saveManageInfo} className="bg-accent hover:bg-accent/90 text-accent-foreground">Enregistrer l'identité</Button>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Add user dialog (nested) */}
      <Dialog open={openAddUser} onOpenChange={setOpenAddUser}>
        <DialogContent data-testid="dialog-add-user-school">
          <DialogHeader><DialogTitle>Ajouter un utilisateur à {manageSchool?.name}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Photo de profil</Label>
              <div className="mt-1.5">
                <PhotoUploader value={newUserForm.avatar_url} onChange={v => setNewUserForm({ ...newUserForm, avatar_url: v })} kind="avatar" size={64} />
              </div>
            </div>
            <div><Label>Nom complet</Label><Input value={newUserForm.name} onChange={e => setNewUserForm({ ...newUserForm, name: e.target.value })} data-testid="input-newuser-name" /></div>
            <div><Label>Email</Label><Input type="email" value={newUserForm.email} onChange={e => setNewUserForm({ ...newUserForm, email: e.target.value })} data-testid="input-newuser-email" /></div>
            <div><Label>Mot de passe initial</Label><Input value={newUserForm.password} onChange={e => setNewUserForm({ ...newUserForm, password: e.target.value })} data-testid="input-newuser-password" /></div>
            <div><Label>Téléphone</Label><Input value={newUserForm.phone} onChange={e => setNewUserForm({ ...newUserForm, phone: e.target.value })} placeholder="+226 70..." /></div>
            <div>
              <Label>Rôle</Label>
              <select value={newUserForm.role} onChange={e => setNewUserForm({ ...newUserForm, role: e.target.value })} className="w-full mt-1.5 p-2 border border-border rounded-md bg-background text-sm" data-testid="select-newuser-role">
                {CREATABLE_ROLES.map(r => <option key={r} value={r}>{ROLE_FR[r]}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAddUser(false)}>Annuler</Button>
            <Button onClick={addUserToSchool} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-save-newuser">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [purgeSummary, setPurgeSummary] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [r, t] = await Promise.all([
          api.get("/admin/analytics"),
          api.get("/admin/analytics/trends?months=6"),
        ]);
        setData(r.data); setTrends(t.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);
  const exportXlsx = async () => {
    try {
      const r = await api.get("/admin/export/analytics.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `analytics-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doPurge = async (dry) => {
    try {
      setPurging(true);
      const r = await api.post(`/admin/purge-test-data?dry_run=${dry}`);
      setPurgeSummary(r.data);
      if (!dry) {
        toast.success(`Purge : ${r.data.schools} écoles · ${r.data.users} users · ${r.data.students} élèves · ${r.data.applications} candidatures`);
        const [analytics2, trends2] = await Promise.all([
          api.get("/admin/analytics"),
          api.get("/admin/analytics/trends?months=6"),
        ]);
        setData(analytics2.data); setTrends(trends2.data);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPurging(false); }
  };
  if (loading) return <div className="text-sm text-muted-foreground">Chargement…</div>;
  if (!data) return null;
  const t = data.totals;
  const totalCountry = Object.values(data.schools_by_country).reduce((a,b)=>a+b,0);
  const maxRev = trends ? Math.max(1, ...trends.global.map(g => g.revenue)) : 1;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Mois de référence : <b>{data.month}</b> · toutes les écoles du réseau</div>
        <div className="flex gap-2">
          <Button onClick={() => doPurge(true)} disabled={purging} variant="outline" size="sm" data-testid="btn-purge-dry">
            Analyser données test
          </Button>
          <Button onClick={exportXlsx} className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full" data-testid="btn-export-analytics">
            <DownloadSimple size={16} className="mr-2" /> Exporter Excel
          </Button>
        </div>
      </div>
      {purgeSummary && (
        <Card className="p-4 border-accent/30 bg-accent/5" data-testid="purge-summary">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm">
              <b>Données de test {purgeSummary.dry_run ? "détectées" : "purgées"}</b> — {purgeSummary.schools} écoles · {purgeSummary.users} utilisateurs · {purgeSummary.students} élèves · {purgeSummary.applications} candidatures
            </div>
            {purgeSummary.dry_run && (purgeSummary.schools + purgeSummary.users + purgeSummary.students + purgeSummary.applications) > 0 && (
              <Button size="sm" onClick={() => { if (window.confirm("Confirmer la suppression définitive ?")) doPurge(false); }} disabled={purging} className="bg-accent text-accent-foreground" data-testid="btn-purge-confirm">
                <Trash size={14} className="mr-1" /> {purging ? "Purge…" : "Confirmer et supprimer"}
              </Button>
            )}
          </div>
        </Card>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat label="Établissements" v={t.schools} tint="primary" testid="an-schools" />
        <MiniStat label="Élèves actifs" v={t.students} tint="secondary" testid="an-students" />
        <MiniStat label="Enseignants" v={t.teachers} tint="accent" testid="an-teachers" />
        <MiniStat label="Classes" v={t.classes} tint="primary" testid="an-classes" />
        <MiniStat label="Candidatures" v={t.applications} tint="primary" testid="an-apps" />
        <MiniStat label="Acceptées" v={t.accepted_applications} tint="secondary" testid="an-accepted" />
        <MiniStat label="Encaissements mois" v={t.month_revenue_all.toLocaleString("fr-FR")} tint="secondary" testid="an-revenue" />
        <MiniStat label="Impayés global" v={t.unpaid_balance_all.toLocaleString("fr-FR")} tint="accent" testid="an-unpaid" />
      </div>

      {/* Trends chart */}
      {trends && (
        <Card className="p-5" data-testid="an-trends">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Évolution des encaissements</div>
              <div className="text-[11px] text-muted-foreground">6 derniers mois · toutes écoles</div>
            </div>
            <div className="font-heading text-xl font-bold tabular-nums" data-testid="an-trends-total">
              {trends.global.reduce((s, g) => s + g.revenue, 0).toLocaleString("fr-FR")}
            </div>
          </div>
          <div className="h-40 flex items-end gap-2">
            {trends.global.map((g, i) => {
              const h = (g.revenue / maxRev) * 100;
              return (
                <div key={g.month} className="flex-1 flex flex-col items-center gap-1" data-testid={`an-trend-${g.month}`}>
                  <div className="text-[10px] tabular-nums text-muted-foreground">{g.revenue > 0 ? (g.revenue >= 1000 ? `${Math.round(g.revenue/1000)}k` : g.revenue) : ""}</div>
                  <div
                    className="w-full rounded-t-md bg-gradient-to-t from-primary to-primary/40 relative group cursor-help"
                    style={{ height: `${Math.max(h, 3)}%`, transition: "height 400ms" }}
                    title={`${g.month} · ${g.revenue.toLocaleString("fr-FR")}`}
                  >
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] bg-foreground text-background rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 whitespace-nowrap">
                      {g.revenue.toLocaleString("fr-FR")}
                    </div>
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground">{g.month.slice(5)}</div>
                </div>
              );
            })}
          </div>
          {trends.per_school.length > 1 && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Par école (dernier mois)</div>
              <div className="space-y-1.5">
                {trends.per_school.map(s => {
                  const last = s.points[s.points.length - 1]?.revenue || 0;
                  const pct = maxRev ? Math.round((last / maxRev) * 100) : 0;
                  return (
                    <div key={s.school_id} className="flex items-center gap-2 text-xs" data-testid={`an-trend-school-${s.school_id}`}>
                      <span className="w-32 truncate">{s.school_name}</span>
                      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: s.color }} />
                      </div>
                      <span className="w-24 text-right font-mono tabular-nums">{last.toLocaleString("fr-FR")}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      )}

      <Card className="p-5">
        <div className="text-xs uppercase tracking-widest text-secondary font-semibold mb-3">Répartition par pays</div>
        <div className="space-y-2">
          {Object.entries(data.schools_by_country).sort((a,b)=>b[1]-a[1]).map(([k,v]) => {
            const pct = totalCountry ? Math.round(v/totalCountry*100) : 0;
            return (
              <div key={k} data-testid={`an-country-${k}`}>
                <div className="flex justify-between text-xs"><span className="font-mono">{k}</span><span className="tabular-nums text-muted-foreground">{v} · {pct}%</span></div>
                <div className="mt-0.5 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary" style={{width:`${pct}%`}}/></div>
              </div>
            );
          })}
        </div>
      </Card>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2">École</th><th className="px-3 py-2">Pays</th><th className="px-3 py-2">Système</th><th className="px-3 py-2">Devise</th>
            <th className="px-3 py-2 text-right">Élèves</th><th className="px-3 py-2 text-right">Classes</th><th className="px-3 py-2 text-right">Candidat.</th>
            <th className="px-3 py-2 text-right">Encaiss. mois</th><th className="px-3 py-2 text-right">Impayés</th>
          </tr></thead>
          <tbody>
            {data.per_school.map((s,i) => (
              <tr key={s.school_id} className={`border-b border-border last:border-0 ${i%2?"bg-muted/20":""}`} data-testid={`an-row-${s.school_id}`}>
                <td className="px-3 py-2 font-medium">{s.school_name}</td>
                <td className="px-3 py-2 font-mono text-xs">{s.country}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{s.education_system}</td>
                <td className="px-3 py-2 font-mono text-xs">{s.currency}</td>
                <td className="px-3 py-2 text-right tabular-nums">{s.students}</td>
                <td className="px-3 py-2 text-right tabular-nums">{s.classes}</td>
                <td className="px-3 py-2 text-right tabular-nums">{s.applications}</td>
                <td className="px-3 py-2 text-right tabular-nums font-mono">{s.month_revenue.toLocaleString("fr-FR")}</td>
                <td className="px-3 py-2 text-right tabular-nums font-mono text-accent">{s.unpaid_balance.toLocaleString("fr-FR")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function MiniStat({ label, v, tint, testid }) {
  const styles = {
    primary: "bg-primary/10 text-primary",
    secondary: "bg-secondary/10 text-secondary",
    accent: "bg-accent/10 text-accent",
  };
  return (
    <Card className="p-3" data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-widest ${styles[tint]||""} inline-block px-2 py-0.5 rounded`}>{label}</div>
      <div className="font-heading text-xl md:text-2xl font-bold tabular-nums mt-1">{v}</div>
    </Card>
  );
}
